import { z } from "zod";
import Connector, { IConnectorRow } from "../models/Connector";
import { FieldValues } from "react-hook-form";

const siteSchema = z.object(
  {
    siteId: z
      .string()
      .min(1, {
        message: "connectors:siteIdRequired",
      })
      .uuid({
        message: "connectors:siteIdInvalid",
      }),
  },
  {
    required_error: "connectors:siteIdRequired",
    invalid_type_error: "connectors:siteIdRequired",
  }
);

export default class ConnectorRegistry {
  private static _instance: ConnectorRegistry;
  private _connectorSchemas: Record<string, z.ZodTypeAny>;
  private _connectors: Record<string, Connector>;
  private _defaultValues: Record<string, unknown>;
  private _baseSchema: z.AnyZodObject;

  private constructor() {
    this._connectorSchemas = {};
    this._connectors = {};
    this._baseSchema = z.object({
      type: z
        .string({
          required_error: "connectors:typeRequired",
        })
        .min(1, {
          message: "connectors:typeRequired",
        }),
      intervalSeconds: z
        .number({
          required_error: "connectors:intervalRequired",
        })
        .optional()
        .default(604_800),
      ruleSets: z
        .array(
          z.object({
            site: siteSchema,
            rules: z
              .array(
                z.object({
                  attributeName: z.string().min(1, {
                    message: "connectors:attributeNameRequired",
                  }),
                  attributeValue: z.string().min(1, {
                    message: "connectors:attributeValueRequired",
                  }),
                })
              )
              .optional()
              .default([]),
          })
        )
        .min(1, {
          message: "connectors:siteMappingRuleRequired",
        }),
      shouldSaveNoMedia: z.boolean().default(false),
    });
    this._defaultValues = {
      type: "",
      intervalSeconds: 86_400, // 1 day
      ruleSets: [
        {
          siteId: "",
          rules: [],
        },
      ],
      shouldSaveNoMedia: false,
    };
  }

  public registerConnector<
    TFormValues extends FieldValues = FieldValues,
    TConnectorRow extends IConnectorRow = IConnectorRow
  >(
    type: string,
    connector: Connector<TFormValues, TConnectorRow>,
    schema: z.ZodTypeAny
  ) {
    this._connectors[type] = connector as Connector<FieldValues>;
    const connectorNames = Object.keys(this._connectors);
    this._defaultValues[type] = undefined;
    this._baseSchema = this._baseSchema
      .omit({
        type: true,
      })
      .extend({
        type: z.enum(connectorNames as [string, ...string[]], {
          errorMap: (issue, ctx) => {
            if (
              issue.code === z.ZodIssueCode.invalid_type &&
              issue.received === "undefined"
            ) {
              return {
                message: "connectors:typeRequired",
              };
            }
            if (
              issue.code === z.ZodIssueCode.invalid_type ||
              issue.code === z.ZodIssueCode.invalid_enum_value
            ) {
              return {
                message: "connectors:invalidType",
              };
            }
            return { message: ctx.defaultError };
          },
        }),
      });
    this._connectorSchemas[type] = schema.optional();
  }

  public get connectorMap() {
    return this._connectors;
  }

  public get defaultValues() {
    return this._defaultValues;
  }

  public get schema() {
    const schema = this._baseSchema.extend(this._connectorSchemas);
    return schema.refine((data) => {
      if (!data[data.type]) {
        return false;
      }
      return true;
    });
  }

  public static getInstance() {
    if (!this._instance) {
      this._instance = new ConnectorRegistry();
    }
    return this._instance;
  }
}
